import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Wordmark } from "@/components/landing/shared";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import MfaFlow from "@/components/MfaFlow";

export default function Login() {
  const { login, refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    // Normalizzazione identificativo: elimina SOLO whitespace iniziale/finale;
    // la password non viene MAI modificata. Il campo visualizzato viene
    // aggiornato con il valore normalizzato. noValidate sul form evita che la
    // validazione HTML5 nativa blocchi il submit con spazi accidentali.
    const normalizedEmail = email.trim();
    if (normalizedEmail !== email) setEmail(normalizedEmail);
    setLoading(true);
    try {
      const data = await login(normalizedEmail, password);
      if (data?.status?.startsWith("MFA_")) {
        setMfa(data);
        return;
      }
      navigate(data?.is_superadmin ? "/admin/platform" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="login-page">
      <div className="hidden lg:flex w-[42%] bg-[#0B192C] text-white flex-col justify-between p-12">
        <div>
          <span className="inline-block bg-white rounded-xl px-4 py-2.5">
            <Wordmark />
          </span>
        </div>
        <div>
          <h1 className="text-3xl font-bold leading-snug mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>
            {t("login.heroTitle")}
          </h1>
          <p className="text-slate-300 leading-relaxed text-sm max-w-md">
            {t("login.heroText")}
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-8">
              {mfa ? (
                <MfaFlow status={mfa.status} mfaToken={mfa.mfa_token}
                  onSuccess={async () => { const me = await refresh(); navigate(me?.is_superadmin ? "/admin/platform" : "/dashboard"); }}
                  onBack={() => { setMfa(null); setPassword(""); }} />
              ) : (
              <>
              <div className="lg:hidden mb-6">
                <Wordmark />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-1">{t("login.title")}</h2>
              <p className="text-sm text-slate-600 mb-6">{t("login.subtitle")}</p>

              {error && (
                <div role="alert" data-testid="login-error" className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-800">
                  {error}
                </div>
              )}

              <form onSubmit={submit} className="space-y-4" noValidate>
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input id="login-email" type="email" autoComplete="email" required
                    data-testid="login-email-input" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => { const n = email.trim(); if (n !== email) setEmail(n); }} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <Input id="login-password" type="password" autoComplete="current-password" required
                    data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-green-700 hover:bg-green-800" data-testid="login-submit-button">
                  {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" /> {t("login.signingIn")}</>) : t("login.title")}
                </Button>
              </form>

              <p className="text-sm text-slate-600 mt-6 text-center">
                {t("login.noAccount")}{" "}
                <Link to="/register" className="text-green-700 font-semibold hover:underline" data-testid="go-to-register-link">
                  {t("login.register")}
                </Link>
              </p>
              <p className="text-[11px] text-slate-500 mt-3 text-center"
                data-testid="forgot-password-hint">
                Password dimenticata? Contatta l'amministratore della piattaforma:
                può generare un link di reset sicuro e monouso dalla console.
              </p>
              </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
